// Application state — ported from the original app's `S` object, minus everything the
// rebuild explicitly drops (water tracking, AI trainer/API settings).
//
// IMPORTANT: Firestore is now the authoritative source of truth (see lib/sync.js).
// localStorage (lib/storage.js) is only an offline-read cache. `S` itself is kept as a
// single stable object reference for the whole app's lifetime — load() and the sync layer
// both mutate it in place (never reassign the binding) so every module that imported
// `{ S }` always sees the current data without needing a setter.
import { readCache, writeCache } from './lib/storage.js';
import { DISH_DB } from './data/dishes.js';
import { VITS_DEFAULT } from './data/vitamins.js';
import { today } from './lib/dates.js';

export const MEALS = ['breakfast', 'lunch', 'dinner', 'snacks'];
export const MEAL_LABELS = { breakfast: 'Завтрак', lunch: 'Обед', dinner: 'Ужин', snacks: 'Перекус' };

// Archive password gate — unchanged from the original (hardcoded, not a real security
// boundary, just friction against accidental edits to historical/locked data).
export const ARCHIVE_PASSWORD = '1234';

function defaultState() {
  return {
    weightLog: [],
    foodLog: {},
    workoutLog: [],
    weeklyPlan: {}, // {'YYYY-MM-DD': {rest, cardio, strength}} — next 7 real dates, see pages/workout.js
    measurements: { waist: [], chest: [], hips: [], biceps: [], fat: [] },
    customDishes: [],
    customIngs: null,
    ingAvailable: {},
    lastGrams: {}, // {productName: gramsLastUsed}
    lastRecipe: {}, // {dishName: {ingredients, cookedWeight, noShrink}} — see pages/food.js selectFoodItem
    vitamins: {},
    vitConfig: null,
    hasExported: false,
    templates: [],
    archivedDays: [],
    customQuotes: { morning: [], day: [], evening: [] },
    settings: {
      userName: '',
      theme: 'light',
      goalKcal: 2100,
      goalP: 120,
      goalF: 75,
      goalC: 230,
      notifyEnabled: true,
      exportEnabled: true,
      showVitOnRest: false,
      weightStatMode: 'toGoal', // 'toGoal' | 'week' | 'total' — see pages/home.js renderWeightSection
      hideWeight: false, // chart + archive privacy — unrelated to weightShow below
      weightShow: { current: true, diff: true, stat: true, progress: true, nextForecast: true }, // dashboard cells, independently, see pages/home.js
      tgToken: '',
      tgChatId: '',
      customIconUrl: undefined,
    },
    startWeight: 100,
    goalWeight: 90,
  };
}

// Stable object reference — every module imports this same object.
export const S = defaultState();

function runMigrations() {
  // Past days lock themselves automatically — editing them still requires the archive
  // password gate, same protection as before.
  const td0 = today();
  Object.keys(S.foodLog).forEach(d => {
    if (d < td0 && !S.archivedDays.includes(d)) S.archivedDays.push(d);
  });

  // One-time cleanup: an old saveWorkout() bug could leave two entries for the same date.
  // Keep one per date — prefer an entry with actual cardio/strength data over a bare rest
  // flag, otherwise keep the last one. Cheap and idempotent, kept as a standing defense
  // even though the new saveWorkout() (see pages/workout.js) can no longer create the dupe.
  if (!S.hasDedupedWorkouts) {
    const byDate = {};
    S.workoutLog.forEach(w => {
      const existing = byDate[w.date];
      if (!existing) byDate[w.date] = w;
      else {
        const existingIsReal = existing.cardio || existing.strength;
        const newIsReal = w.cardio || w.strength;
        if (newIsReal || !existingIsReal) byDate[w.date] = w;
      }
    });
    S.workoutLog = Object.values(byDate);
    S.hasDedupedWorkouts = true;
  }

  // One-time cleanup: a rest day should never also carry a workout type (an old
  // health-import bug could mark a manually-set "Выходной" day as cardio too).
  if (!S.hasFixedRestCardioConflict) {
    S.workoutLog.forEach(w => {
      if (w.rest) { w.cardio = false; w.strength = null; }
    });
    S.hasFixedRestCardioConflict = true;
  }

  // Clean up dishes that are exact byte-for-byte duplicates of one of our own known seed
  // items (matched by id, not just by name) — never touches anything the person added or
  // edited themselves, even if its name happens to match a built-in product.
  if (S.customDishes && S.customDishes.length) {
    const seedIds = new Set(DISH_DB.map(d => d.id));
    const seedNames = new Set(DISH_DB.filter(d => d.ingredients && d.ingredients.length === 1).map(d => d.name));
    S.customDishes = S.customDishes.filter(d => {
      const isKnownSeedDup = seedIds.has(d.id) && seedNames.has(d.name) && !d.favorite;
      return !isKnownSeedDup;
    });
  }

  if (!S.vitConfig) S.vitConfig = JSON.parse(JSON.stringify(VITS_DEFAULT));

  // Migrate very old workout format (acts array) if it's ever encountered.
  S.workoutLog = (S.workoutLog || []).map(w => {
    if (w.acts !== undefined) {
      const cardioTypes = ['Элипсоид', 'Ходьба', 'Обруч', 'Скакалка'];
      const strMap = { Верх: 'Верх', Ноги: 'Ноги', Пресс: 'Пресс' };
      return {
        date: w.date,
        cardio: (w.acts || []).some(a => cardioTypes.includes(a)),
        strength: (w.acts || []).map(a => strMap[a]).filter(Boolean)[0] || null,
        kcalBurned: 0, steps: 0, duration: 0, note: w.note || '',
      };
    }
    return w;
  });

  // Defensive defaults for fields that may be missing on older cached/synced documents.
  if (!S.measurements) S.measurements = { waist: [], chest: [], hips: [], biceps: [], fat: [] };
  if (!S.measurements.fat) S.measurements.fat = [];
  if (!S.customQuotes) S.customQuotes = { morning: [], day: [], evening: [] };
  if (!S.weeklyPlan) S.weeklyPlan = {};
  if (!S.settings.weightShow) S.settings.weightShow = { current: true, diff: true, stat: true, progress: true, nextForecast: true };
  if (S.settings.weightShow.nextForecast === undefined) S.settings.weightShow.nextForecast = true;
  if (!S.lastRecipe) S.lastRecipe = {};
}

/** Populate `S` in place from a plain object (cache read or Firestore snapshot). */
export function applyState(obj) {
  Object.keys(S).forEach(k => delete S[k]);
  Object.assign(S, defaultState(), obj || {});
  S.settings = Object.assign(defaultState().settings, (obj && obj.settings) || {});
  runMigrations();
}

/** Load from the local offline cache (used before Firestore has answered, or if never signed in). */
export function loadFromCache() {
  const cached = readCache();
  if (cached) {
    applyState(cached);
  } else {
    // First-ever local boot before any Firestore data has arrived — a genuinely blank slate.
    // (No historical demo data is seeded here — that one-time seed was specific to this
    // person's original onboarding and already lives in their Firestore account.)
    applyState(defaultState());
    S.customDishes = JSON.parse(JSON.stringify(DISH_DB));
    S.vitConfig = JSON.parse(JSON.stringify(VITS_DEFAULT));
  }
}

export { runMigrations };

/** Persist current `S` to the local cache immediately (call after any mutation). */
export function saveCache() {
  writeCache(S);
}
